import { isString } from 'underscore';
import * as uuid from 'uuid';

import { DashboardApiModel } from '../../../api/dashboard/models/dashboard-api.model';
import { DashboardBlockApiModel } from '../../../api/dashboard/models/dashboard-block-api.model';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportBlockTypeApiEnum } from '../../../api/reports/enum/report-block-type-api.enum';

import { ChartDisplayElementModel } from '../../Reports/models/chart-display-element.model';
import { ReportSeriesModel } from '../../Reports/models/report-series.model';
import { dashboardsBlockTypes, reportsBlockTypes } from '../../Reports/models/reports-block-types.model';
import { ReportBlockTypeEnum, reportBlockTypeEnumFromApi,
    reportBlockTypeEnumToApi, reportBlockTypeEnumToDefaultChartSpecificType } from '../../Reports/enums/report-block-type.enum';
import { SeriesTypeEnum } from '../../Reports/enums/series-type.enum';
import { ReportDateRangeModel } from '../../Reports/models/report-date-range.model';
import { ChartMetricsEnum } from '../../Reports/enums/chart-metrics.enum';
import { ChartSpecificTypesEnum } from '../../Reports/enums/chart-specific-types.enum';

import { BlockModel } from '../../PageBuilder/models/block.model';
import { BlockTypeModel } from '../../PageBuilder/models/block-type.model';
import { ChartStylesModel } from '../../Charts/models/chart-styles.model';
import { DimensionsModel } from '../../Misc/models/dimensions.model';
import { ChartContentFilterModel } from './chart-content-filter.model';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { ChartBlockComponent } from '../../Reports/components/ChartBlock/chart-block.component';
import { CoverageSummaryChartBlockComponent } from '../../Reports/components/CoverageSummaryChartBlock/coverage-summary-chart-block.component';
import { ChartDisplaySettingsModel } from '../../Charts/models/chart-display-settings.model';
import { Mention } from '../../Mentions/models/mention';

import { DashboardCoverageSummaryChartBlockComponent } from '../components/DashboardCoverageSummaryChartBlock/dashboard-coverage-summary-chart-block.component';
import { DashboardChartBlockComponent } from '../components/DashboardChartBlock/dashboard-chart-block.component';
import { TopTermChartBlockComponent } from '../../Reports/components/TopTermChartBlock/top-term-chart-block.component';
import { DashboardTopPostsChartBlockComponent } from '../components/DashboardTopPostsChartBlock/dashboard-top-posts-chart-block.component';
import { TopPostChartBlockComponent } from '../../Reports/components/TopPostChartBlock/top-post-chart-block.component';
import { DashboardTopTermChartBlockComponent } from '../components/DashboardTopTermChartBlock/dashboard-top-term-chart-block.component';
import { TopMentionsChartBlockComponent } from '../../Reports/components/TopMentions/TopMentionsChartBlock/top-mentions-chart-block.component';
import { DashboardTopMentionsChartBlockComponent } from '../components/DashboardTopMentionsChartBlock/dashboard-top-mentions-chart-block.component';
import { MentionCustomizationModel } from '../../../Monitor/Shared/models/mention-customization.model';

export class DashboardModel {
    id: number;
    blocks: BlockModel[];

    constructor(apiModel?: DashboardApiModel) {
        if (apiModel) {
            this.id = apiModel.id;
            if (apiModel.data && isString(apiModel.data)) {
                const blocks = JSON.parse(apiModel.data);
                this.blocks = this.mapBlocksFromApi(blocks);
                if (!this.blocks) {
                    this.blocks = [];
                }
            }
        } else {
            this.id = null;
            this.blocks = [];
        }
    }


    /**
     * Converts DashboardBlockApiModel[] to BlockModel[]
     */
    protected mapBlocksFromApi(apiBlocks: DashboardBlockApiModel[] = []): BlockModel[] {
        const blocks: BlockModel[] = [];

        const dashboardBlockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(dashboardsBlockTypes);
        const reportBlockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(reportsBlockTypes);

        for (const apiBlock of apiBlocks) {
            const dashboardBlockType = dashboardBlockTypeMap.get(reportBlockTypeEnumFromApi.get(apiBlock.type));
            let reportBlockType = reportBlockTypeMap.get(reportBlockTypeEnumFromApi.get(apiBlock.type));

            if (dashboardBlockType) {
                const block: BlockModel = new BlockModel(
                    apiBlock.id,
                    dashboardBlockType,
                    new (dashboardBlockType.displayElementType)()
                );
                dashboardBlockType.isDashboard = true;

                block.rows = apiBlock.rows;
                block.cols = apiBlock.cols;
                block.x = apiBlock.x;
                block.y = apiBlock.y;

                if (reportBlockTypeEnumFromApi.has(apiBlock.type)) {
                    const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                    const data = new ChartStylesModel(apiBlock.data);

                    chartDisplayElement.seriesCount = data.seriesCount;
                    chartDisplayElement.styles = new ChartStylesModel(data.styles);

                    chartDisplayElement.numOfItems = data.numOfItems;

                    if (apiBlock.type === ReportBlockTypeApiEnum.RECENT_SURGE_ALERTS) {
                        chartDisplayElement.dateRange = data.dateRange;
                    }
                    chartDisplayElement.contentFilters = new ChartContentFilterModel(data.contentFilters);

                    if (chartDisplayElement.seriesCount) {
                        const series = new ReportSeriesModel(null, reportBlockTypeEnumFromApi.get(apiBlock.type), data.type);
                        chartDisplayElement.series = [];

                        for (let i = 0; i < chartDisplayElement.seriesCount; i++) {
                            const newSeries = series.clone();
                            newSeries.seriesUniqueUuid = uuid.v4();
                            newSeries.dataIndex = i;
                            if (chartDisplayElement.styles?.colorMap) {
                                const colorKey = ReportSeriesModel.getSeriesColorKeyPrefix() + i;
                                if (chartDisplayElement.styles?.colorMap[colorKey]) {
                                    newSeries.color = chartDisplayElement.styles?.colorMap[colorKey];
                                }
                            }

                            newSeries.dateRange = new ReportDateRangeModel(data.dateRange);
                            newSeries.chartType = reportBlockTypeEnumFromApi.get(apiBlock.type);
                            chartDisplayElement.series.push(newSeries);
                        }
                    }

                    chartDisplayElement.title = apiBlock.title;
                    chartDisplayElement.size = new DimensionsModel(block.rows, block.cols);
                    chartDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel(data.chartDisplaySettings);

                    block.displayElement = chartDisplayElement;
                }

                blocks.push(block);
            } else if (reportBlockType) {
                // must clone it first, or it will change it on the reports page as well
                reportBlockType = reportBlockType.clone();
                reportBlockType.isDashboard = true;
                // change report componenent type to use the dashboard chart blocks component

                switch (reportBlockType.componentType) {
                    case ChartBlockComponent:
                        reportBlockType.componentType = DashboardChartBlockComponent;
                        break;
                    case TopTermChartBlockComponent:
                        reportBlockType.componentType = DashboardTopTermChartBlockComponent;
                        break;
                    case CoverageSummaryChartBlockComponent:
                        reportBlockType.componentType = DashboardCoverageSummaryChartBlockComponent;
                        break;
                    case TopPostChartBlockComponent:
                        reportBlockType.componentType = DashboardTopPostsChartBlockComponent;
                        break;
                    case TopMentionsChartBlockComponent:
                        reportBlockType.componentType = DashboardTopMentionsChartBlockComponent;
                        break;
                    default:
                        reportBlockType.componentType = DashboardChartBlockComponent;
                        break;
                }

                const block: BlockModel = new BlockModel(
                    apiBlock.id,
                    reportBlockType,
                    new (reportBlockType.displayElementType)()
                );

                block.rows = apiBlock.rows;
                block.cols = apiBlock.cols;
                block.x = apiBlock.x;
                block.y = apiBlock.y;

                if (reportBlockTypeEnumFromApi.has(apiBlock.type)) {
                    const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                    const data = new ChartStylesModel(apiBlock.data);
                    chartDisplayElement.metric = data.metric;
                    chartDisplayElement.metrics = data.metrics;
                    // Backwards compatible metrics for old charts with no values
                    if (block.type.key === ReportBlockTypeEnum.COVERAGE_SUMMARY && (!data.metrics || data.metrics.length === 0)) {
                        chartDisplayElement.metrics = [ChartMetricsEnum.volumeOfMentions, ChartMetricsEnum.audienceReach, ChartMetricsEnum.ave];
                    }
                    chartDisplayElement.sentiments = data.sentiments;
                    chartDisplayElement.labelDisplayOptions = data.labelDisplayOptions;
                    chartDisplayElement.stackedChartTypes = data.stackedChartTypes;
                    chartDisplayElement.referralTypes = data.referralTypes;
                    chartDisplayElement.prevents = data.prevents;
                    chartDisplayElement.termType = data.termType;
                    if (data.termChartType && !data.chartSpecificType) {
                        // back compatibility
                        chartDisplayElement.chartSpecificType = data.termChartType;
                    } else {
                        chartDisplayElement.chartSpecificType = data.chartSpecificType;
                    }
                    if (data.chartSpecificType) {
                        chartDisplayElement.chartSpecificType = data.chartSpecificType;
                    } else {
                        if (data.termChartType) {
                            chartDisplayElement.chartSpecificType = data.termChartType;
                        } else {
                            chartDisplayElement.chartSpecificType = reportBlockTypeEnumToDefaultChartSpecificType.get(block.type.key as ReportBlockTypeEnum);
                        }
                    }
                    chartDisplayElement.numOfItems = data.numOfItems;
                    chartDisplayElement.mapChartColorScheme = data.mapChartColorScheme;
                    chartDisplayElement.mapChartChoroplethInterpolationType = data.mapChartChoroplethInterpolationType;
                    chartDisplayElement.timeInterval = data.timeInterval;
                    chartDisplayElement.topMentionsSortField = data.topMentionsSortField;
                    chartDisplayElement.topMentionsMetadata = new MentionCustomizationModel(data.topMentionsMetadata);
                    chartDisplayElement.topCuratedMentions = data.topCuratedMentions?.map(x => new Mention(x)) || [];

                    chartDisplayElement.series = apiBlock.series.map(
                        reportApiSeriesModel => new ReportSeriesModel(
                            reportApiSeriesModel,
                            reportBlockTypeEnumFromApi.get(apiBlock.type),
                            null,
                            data.metric,
                            chartDisplayElement
                        )
                    );

                    if (chartDisplayElement.chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
                        block.type = block.type.clone();
                        block.type.componentType = DashboardChartBlockComponent;
                    }

                    for (const series of chartDisplayElement.series) {
                        if (data.sentiments) {
                            series.sentiments = data.sentiments;
                        }
                    }

                    // set the color property of each series, if it exists in the colormap
                    for (let i = 0; i < chartDisplayElement.series.length; i++) {
                        const color = data.colorMap[ReportSeriesModel.getSeriesColorKeyPrefix() + i];

                        if (color) {
                            chartDisplayElement.series[i].color = color;
                        }
                    }

                    // if the data is older it won't contain options for sentiment or label display
                    // provide the defaults here:
                    if (!data.labelDisplayOptions) {
                        chartDisplayElement.labelDisplayOptions = [
                            LabelDisplayOptionsEnum.percent
                        ];
                    }

                    if (!data.sentiments) {
                        chartDisplayElement.sentiments = [
                            SearchCriteriaQuerySentimentsEnum.positive,
                            SearchCriteriaQuerySentimentsEnum.neutral,
                            SearchCriteriaQuerySentimentsEnum.negative,
                            SearchCriteriaQuerySentimentsEnum.notAvailable
                        ];
                    }

                    if (!data.referralTypes) {
                        if (block.type.key === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND) {
                            chartDisplayElement.referralTypes = [
                                GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS,
                                GoogleAnalyticsReportTypesEnum.SESSIONS,
                                GoogleAnalyticsReportTypesEnum.USERS
                            ];
                        } else if (block.type.key === ReportBlockTypeEnum.REFERRAL_TREND) {
                            chartDisplayElement.referralTypes = [
                                GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS,
                                GoogleAnalyticsReportTypesEnum.WEB_REFERRALS,
                                GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS,
                                GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS
                            ];
                        }
                    }

                    // label display options were briefly stored directly on the 'checkboxOptions' property instead
                    // of on 'labelDisplayOptions' - this is a compatibility layer
                    if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.percent)) {
                        chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.percent);
                        data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.percent), 1);
                    }

                    if (data.checkboxOptions && data.checkboxOptions.includes(LabelDisplayOptionsEnum.count)) {
                        chartDisplayElement.labelDisplayOptions.push(LabelDisplayOptionsEnum.count);
                        data.checkboxOptions.splice(data.checkboxOptions.indexOf(LabelDisplayOptionsEnum.count), 1);
                    }

                    delete data.sentiments;
                    delete data.labelDisplayOptions;
                    delete data.referralTypes;
                    delete data.prevents;

                    chartDisplayElement.styles = new ChartStylesModel(data);
                    chartDisplayElement.title = apiBlock.title;
                    chartDisplayElement.size = new DimensionsModel(block.rows, block.cols);
                    chartDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel(data.chartDisplaySettings);
                    block.displayElement = chartDisplayElement;
                }

                blocks.push(block);
            } else {
                return blocks;
            }
        }

        return blocks;
    }

    /**
     * Converts the BlockTypeModel[] into a map for lookup efficiency
     */
    protected makeBlockTypeMap(blockTypes: BlockTypeModel[]): Map<ReportBlockTypeEnum, BlockTypeModel> {
        const map = new Map<ReportBlockTypeEnum, BlockTypeModel>();

        blockTypes.forEach(
            blockType => {
                map.set(blockType.key as ReportBlockTypeEnum, blockType);
            }
        );

        return map;
    }

    public getSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = this.blocks.flatMap(block =>
            (block.displayElement as ChartDisplayElementModel)?.series ?? []
        );

        return series;
    }

    public getMentionsSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const mentionSeries = (block.displayElement as ChartDisplayElementModel)?.getMentionSeries();
            if (mentionSeries) {
                series.push(...mentionSeries);
            }
        }

        return series;
    }

    public getSocialSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const socialSeries = (block.displayElement as ChartDisplayElementModel)?.getSocialSeries();
            if (socialSeries) {
                series.push(...socialSeries);
            }
        }

        return series;
    }

    public getExternalDataSeries(): ReportSeriesModel[] {
        const series: ReportSeriesModel[] = [];

        for (const block of this.blocks) {
            const externalDataSeries = (block.displayElement as ChartDisplayElementModel)?.getExternalDataSeries();
            if (externalDataSeries) {
                series.push(...externalDataSeries);
            }
        }

        return series;
    }

    toApiModel(): DashboardApiModel {
        const blocks = this.mapBlocksToApi(this.blocks);
        const apiModel: DashboardApiModel = {
            data: JSON.stringify(blocks),
        };
        if (this.id) {
            apiModel.id = this.id;
        }
        return apiModel;
    }

    clone(): DashboardModel {
        const newBlock = new DashboardModel();
        newBlock.id = this.id;
        newBlock.blocks = this.blocks.map(block => block.clone());

        return newBlock;
    }

    protected mapBlocksToApi(blocks: BlockModel[]): DashboardBlockApiModel[] {
        const apiBlocks: DashboardBlockApiModel[] = [];

        const dashboardBlockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(dashboardsBlockTypes);
        const reportBlockTypeMap: Map<ReportBlockTypeEnum, BlockTypeModel> = this.makeBlockTypeMap(reportsBlockTypes);

        for (const block of blocks) {
            if (!block.type || !block.type.key) {
                return;
            }
            const dashboardBlockType = dashboardBlockTypeMap.get(block.type.key as ReportBlockTypeEnum);
            const reportBlockType = reportBlockTypeMap.get(block.type.key as ReportBlockTypeEnum);

            if (reportBlockTypeEnumToApi.has(block.type.key as ReportBlockTypeEnum)) {
                if (dashboardBlockType) {
                    const chartDisplayElement = block.displayElement as ChartDisplayElementModel;
                    const data = {
                        seriesCount: chartDisplayElement.seriesCount,
                        numOfItems: chartDisplayElement.numOfItems,
                        contentFilters: chartDisplayElement.contentFilters,
                        styles: chartDisplayElement.styles,
                        type: null,
                        dateRange: null,
                        chartHeaderSettings: chartDisplayElement.chartHeaderSettings,
                        chartSpecificType : chartDisplayElement.chartSpecificType,
                        termType : chartDisplayElement.termType
                    };
                    if (chartDisplayElement.series.length) {
                        const tempSeries = chartDisplayElement.series[0].toDashboardApiModel();
                        data.type = tempSeries.type;
                        data.dateRange = tempSeries.dateRange;
                    } else if (block.type.key === ReportBlockTypeEnum.RECENT_SURGE_ALERTS) {
                        data.dateRange = chartDisplayElement.dateRange;
                    } else {
                        // hack for now, only topic series on dashboard page
                        // to fix if there is no topics defined in the account
                        data.type = SeriesTypeEnum.topic;
                        data.dateRange = new ReportDateRangeModel();
                    }

                    apiBlocks.push({
                        type: reportBlockTypeEnumToApi.get(block.type.key as ReportBlockTypeEnum),
                        title: chartDisplayElement.title,
                        x: block.x,
                        y: block.y,
                        cols: block.cols,
                        rows: block.rows,
                        data,
                        series: []
                    } as DashboardBlockApiModel);

                } else if (reportBlockType) {
                    const chartDisplayElement = block.displayElement as ChartDisplayElementModel;

                    const data = chartDisplayElement.styles;
                    data.sentiments = chartDisplayElement.sentiments;
                    data.labelDisplayOptions = chartDisplayElement.labelDisplayOptions;
                    data.stackedChartTypes = chartDisplayElement.stackedChartTypes;
                    data.metric = chartDisplayElement.metric;
                    data.referralTypes = chartDisplayElement.referralTypes;
                    data.prevents = chartDisplayElement.prevents;
                    data.chartHeaderSettings = chartDisplayElement.chartHeaderSettings;
                    // newly added since social
                    data.metrics = chartDisplayElement.metrics;
                    data.chartSpecificType = chartDisplayElement.chartSpecificType;
                    data.termType = chartDisplayElement.termType;
                    data.numOfItems = chartDisplayElement.numOfItems;
                    data.mapChartColorScheme = chartDisplayElement.mapChartColorScheme;
                    data.mapChartChoroplethInterpolationType = chartDisplayElement.mapChartChoroplethInterpolationType;
                    data.timeInterval = chartDisplayElement.timeInterval;
                    data.topMentionsSortField = chartDisplayElement.topMentionsSortField;
                    data.topMentionsMetadata = chartDisplayElement.topMentionsMetadata;
                    data.topCuratedMentions = chartDisplayElement.topCuratedMentions?.map(x => x.convertToApiMention());

                    // there can only be one color associated with this series, so delete all other colors that
                    // look like they are meant to associate with *a* series
                    // we'll set the colors just below, per series
                    for (const [key, _] of Object.entries(data.colorMap)) {
                        if (key.startsWith(ReportSeriesModel.getSeriesColorKeyPrefix())) {
                            delete data.colorMap[key];
                        }
                    }

                    for (let i = 0; i < chartDisplayElement.series.length; i++) {
                        const series = chartDisplayElement.series[i];
                        const seriesKey = ReportSeriesModel.getSeriesColorKeyPrefix() + i;

                        // actually set the color for the series in the colorMap, if appropriate
                        if (series.color) {
                            data.colorMap[seriesKey] = series.color;
                        }
                    }

                    apiBlocks.push({
                        type: reportBlockTypeEnumToApi.get(block.type.key as ReportBlockTypeEnum),
                        title: chartDisplayElement.title,
                        x: block.x,
                        y: block.y,
                        cols: block.cols,
                        rows: block.rows,
                        data: JSON.stringify(data.toApi()),
                        series: (chartDisplayElement.series || []).map(series => series.toApiModel())
                    } as DashboardBlockApiModel);

                    delete data.sentiments;
                    delete data.labelDisplayOptions;
                    delete data.referralTypes;
                    delete data.prevents;
                }

            }
        }

        return apiBlocks;
    }

}
